/**
 * EGDC V4 Header — interactive behaviors.
 *
 *   <v4-menu-drawer>    : mobile slide-in drawer
 *   <v4-search-overlay> : predictive search overlay
 *   Plus inline handlers for sticky shrink, mega menu hover/keyboard,
 *   account dropdown, cart bubble pulse.
 */

/* ============================================================
   MOBILE MENU DRAWER
   ============================================================ */
class V4MenuDrawer extends HTMLElement {
  connectedCallback() {
    this.toggle = document.querySelector('[data-menu-toggle]');
    this.closeBtns = this.querySelectorAll('[data-menu-close]');
    this.scrim = this.querySelector('.v4-menu-drawer__scrim');

    if (this.toggle) this.toggle.addEventListener('click', this.open);
    this.closeBtns.forEach((b) => b.addEventListener('click', this.close));
    if (this.scrim) this.scrim.addEventListener('click', this.close);
    document.addEventListener('keydown', this.onKey);
  }

  disconnectedCallback() {
    if (this.toggle) this.toggle.removeEventListener('click', this.open);
    this.closeBtns.forEach((b) => b.removeEventListener('click', this.close));
    if (this.scrim) this.scrim.removeEventListener('click', this.close);
    document.removeEventListener('keydown', this.onKey);
  }

  open = (event) => {
    if (event) event.preventDefault();
    const parentSection = this.closest('.shopify-section');
    if (parentSection) parentSection.classList.add('is-overlay-active');
    this.classList.add('is-open');
    document.body.classList.add('v4-no-scroll');
    this.setAttribute('aria-hidden', 'false');
    if (this.toggle) this.toggle.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => {
      const focusable = this.querySelector('[data-menu-close]');
      if (focusable) focusable.focus();
    });
  };

  close = () => {
    this.classList.remove('is-open');
    document.body.classList.remove('v4-no-scroll');
    this.setAttribute('aria-hidden', 'true');
    if (this.toggle) {
      this.toggle.setAttribute('aria-expanded', 'false');
      this.toggle.focus();
    }
    const parentSection = this.closest('.shopify-section');
    if (parentSection) parentSection.classList.remove('is-overlay-active');
  };

  onKey = (event) => {
    if (event.key === 'Escape' && this.classList.contains('is-open')) this.close();
  };
}
if (!customElements.get('v4-menu-drawer')) customElements.define('v4-menu-drawer', V4MenuDrawer);

/* ============================================================
   PREDICTIVE SEARCH OVERLAY
   ============================================================ */
class V4SearchOverlay extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-search-input]');
    this.form = this.querySelector('[data-search-form]');
    this.empty = this.querySelector('[data-search-empty]');
    this.loading = this.querySelector('[data-search-loading]');
    this.hits = this.querySelector('[data-search-hits]');
    this.noResults = this.querySelector('[data-search-no-results]');
    this.recent = this.querySelector('[data-search-recent]');
    this.recentList = this.querySelector('[data-search-recent-list]');
    this.scrim = this.querySelector('.v4-search-overlay__scrim');
    this.toggles = document.querySelectorAll('[data-search-toggle]');
    this.closeBtns = this.querySelectorAll('[data-search-close]');
    this.lastTrigger = null;

    this.toggles.forEach((t) => t.addEventListener('click', this.open));
    this.closeBtns.forEach((b) => b.addEventListener('click', this.close));
    document.addEventListener('keydown', this.onKey);

    if (this.form) this.form.addEventListener('submit', this.onSubmit);
    if (this.input) this.input.addEventListener('input', this.onInput);

    this.renderRecent();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKey);
  }

  open = (event) => {
    if (event) event.preventDefault();
    if (event && event.currentTarget) this.lastTrigger = event.currentTarget;
    // B1: close mobile menu drawer first if open — otherwise both share
    // body.v4-no-scroll and closing search would unlock body prematurely
    const drawer = document.querySelector('v4-menu-drawer.is-open');
    if (drawer && typeof drawer.close === 'function') drawer.close();
    // Promote the parent shopify-section's z-index so the announcement
    // bar (which sits in a sibling section with z-index: 80) doesn't
    // bleed through and hijack clicks at the top of the overlay.
    const parentSection = this.closest('.shopify-section');
    if (parentSection) parentSection.classList.add('is-overlay-active');
    this.classList.add('is-open');
    document.body.classList.add('v4-no-scroll');
    this.setAttribute('aria-hidden', 'false');
    this.toggles.forEach((t) => t.setAttribute('aria-expanded', 'true'));
    requestAnimationFrame(() => { if (this.input) this.input.focus(); });
  };

  close = () => {
    this.classList.remove('is-open');
    document.body.classList.remove('v4-no-scroll');
    this.setAttribute('aria-hidden', 'true');
    this.toggles.forEach((t) => t.setAttribute('aria-expanded', 'false'));
    const parentSection = this.closest('.shopify-section');
    if (parentSection) parentSection.classList.remove('is-overlay-active');
    if (this.lastTrigger) this.lastTrigger.focus();
  };

  onKey = (event) => {
    if (event.key === 'Escape' && this.classList.contains('is-open')) this.close();
  };

  onSubmit = (event) => {
    const q = this.input && this.input.value.trim();
    if (q) this.pushRecent(q);
  };

  onInput = () => {
    const q = this.input.value.trim();
    clearTimeout(this._t);
    if (!q) {
      this.showState('empty');
      return;
    }
    this._t = setTimeout(() => this.fetch(q), 250);
  };

  fetch(q) {
    this.showState('loading');
    const url =
      '/search/suggest.json?q=' +
      encodeURIComponent(q) +
      '&resources[type]=product,collection,article&resources[limit]=5&resources[options][unavailable_products]=last';
    fetch(url)
      .then((r) => r.json())
      .then((data) => {
        const r = data && data.resources && data.resources.results;
        if (!r) { this.showState('noresults'); return; }
        const total = (r.products && r.products.length) + (r.collections && r.collections.length) + (r.articles && r.articles.length);
        if (!total) { this.showState('noresults'); return; }
        this.renderHits(r, q);
        this.showState('hits');
      })
      .catch(() => this.showState('noresults'));
  }

  renderHits(r, q) {
    let html = '';
    if (r.products && r.products.length) {
      html += '<div class="v4-search-overlay__group"><span class="egdc-kicker">Productos</span><ul class="v4-search-overlay__products">';
      r.products.forEach((p) => {
        const img = p.featured_image && p.featured_image.url
          ? '<img src="' + p.featured_image.url + '" alt="" loading="lazy" width="64" height="64">'
          : '<span class="v4-search-overlay__product-placeholder" aria-hidden="true"></span>';
        const price = typeof p.price === 'string' ? p.price : (p.price && p.price.amount ? Number(p.price.amount).toFixed(2) : '');
        html += '<li><a href="' + p.url + '">' + img + '<span class="v4-search-overlay__product-info"><strong>' + this.escape(p.title) + '</strong>' + (price ? '<span class="v4-search-overlay__product-price">' + price + '</span>' : '') + '</span></a></li>';
      });
      html += '</ul></div>';
    }
    if (r.collections && r.collections.length) {
      html += '<div class="v4-search-overlay__group"><span class="egdc-kicker">Colecciones</span><ul>';
      r.collections.forEach((c) => { html += '<li><a href="' + c.url + '">' + this.escape(c.title) + '</a></li>'; });
      html += '</ul></div>';
    }
    if (r.articles && r.articles.length) {
      html += '<div class="v4-search-overlay__group"><span class="egdc-kicker">Artículos</span><ul>';
      r.articles.forEach((a) => { html += '<li><a href="' + a.url + '">' + this.escape(a.title) + '</a></li>'; });
      html += '</ul></div>';
    }
    html += '<div class="v4-search-overlay__see-all"><a href="/search?q=' + encodeURIComponent(q) + '">Ver todos los resultados →</a></div>';
    this.hits.innerHTML = html;
  }

  showState(state) {
    [this.empty, this.loading, this.hits, this.noResults].forEach((el) => { if (el) el.hidden = true; });
    if (state === 'empty') this.empty.hidden = false;
    else if (state === 'loading') this.loading.hidden = false;
    else if (state === 'hits') this.hits.hidden = false;
    else if (state === 'noresults') {
      // I3: clear stale hits so screen readers + page weight don't carry old results
      if (this.hits) this.hits.innerHTML = '';
      this.noResults.hidden = false;
    }
  }

  /* Recent searches via localStorage */
  RECENT_KEY = 'egdc_search_recent';
  RECENT_MAX = 5;

  getRecent() {
    try { return JSON.parse(localStorage.getItem(this.RECENT_KEY) || '[]'); } catch (e) { return []; }
  }

  pushRecent(term) {
    try {
      let list = this.getRecent().filter((t) => t.toLowerCase() !== term.toLowerCase());
      list.unshift(term);
      list = list.slice(0, this.RECENT_MAX);
      localStorage.setItem(this.RECENT_KEY, JSON.stringify(list));
    } catch (e) {}
  }

  renderRecent() {
    if (!this.recent || !this.recentList) return;
    const list = this.getRecent();
    if (!list.length) { this.recent.hidden = true; return; }
    this.recent.hidden = false;
    this.recentList.innerHTML = list
      .map((t) => '<li><a href="/search?q=' + encodeURIComponent(t) + '">' + this.escape(t) + '</a></li>')
      .join('');
  }

  escape(str) {
    return String(str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  }
}
if (!customElements.get('v4-search-overlay')) customElements.define('v4-search-overlay', V4SearchOverlay);

/* ============================================================
   STICKY SHRINK, MEGA MENU, ACCOUNT DROPDOWN
   ============================================================ */
(function () {
  const topnav = document.querySelector('.topnav');
  if (!topnav) return;

  /* Sticky shrink */
  if (topnav.dataset.stickyMode === 'shrink') {
    const SHRINK_THRESHOLD = 80;
    const updateShrink = () => {
      topnav.classList.toggle('is-shrunk', window.scrollY > SHRINK_THRESHOLD);
    };
    window.addEventListener('scroll', updateShrink, { passive: true });
    updateShrink();
  }

  /* Mega menu — hover + focus on desktop */
  const items = topnav.querySelectorAll('.topnav__menu-item--has-mega');
  const panels = document.querySelectorAll('[data-mega-panel]');
  if (items.length && panels.length) {
    const showPanel = (label) => {
      panels.forEach((p) => p.classList.toggle('is-open', p.dataset.megaPanel === label));
      items.forEach((it) => {
        const a = it.querySelector('a');
        if (a) a.setAttribute('aria-expanded', it.dataset.navLabel === label ? 'true' : 'false');
      });
    };
    const hideAll = () => {
      panels.forEach((p) => p.classList.remove('is-open'));
      items.forEach((it) => {
        const a = it.querySelector('a');
        if (a) a.setAttribute('aria-expanded', 'false');
      });
    };

    let openTimer = null;
    items.forEach((it) => {
      const label = it.dataset.navLabel;
      it.addEventListener('mouseenter', () => {
        clearTimeout(openTimer);
        openTimer = setTimeout(() => showPanel(label), 120);
      });
      it.addEventListener('mouseleave', () => clearTimeout(openTimer));
      it.querySelector('a').addEventListener('focus', () => showPanel(label));
    });
    panels.forEach((p) => {
      p.addEventListener('mouseenter', () => {
        clearTimeout(openTimer);
        p.classList.add('is-open');
      });
      p.addEventListener('mouseleave', hideAll);
    });
    topnav.addEventListener('mouseleave', hideAll);
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') hideAll(); });
  }

  /* Account dropdown (logged-in state) */
  const accountToggle = topnav.querySelector('[data-account-toggle]');
  const accountWrap = topnav.querySelector('[data-account]');
  if (accountToggle && accountWrap) {
    const menu = accountWrap.querySelector('.topnav__account-menu');
    const closeMenu = () => {
      accountWrap.classList.remove('is-open');
      accountToggle.setAttribute('aria-expanded', 'false');
      if (menu) menu.setAttribute('aria-hidden', 'true');
    };
    const openMenu = () => {
      accountWrap.classList.add('is-open');
      accountToggle.setAttribute('aria-expanded', 'true');
      if (menu) menu.setAttribute('aria-hidden', 'false');
    };
    accountToggle.addEventListener('click', (e) => {
      e.preventDefault();
      accountWrap.classList.contains('is-open') ? closeMenu() : openMenu();
    });
    document.addEventListener('click', (e) => {
      if (!accountWrap.contains(e.target)) closeMenu();
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeMenu(); });
  }

  /* Cart bubble pulse on count change (works alongside v4-cart) */
  const bubble = topnav.querySelector('[data-cart-count]');
  if (bubble) {
    let last = parseInt(bubble.textContent, 10) || 0;
    const observer = new MutationObserver(() => {
      const current = parseInt(bubble.textContent, 10) || 0;
      if (current !== last) {
        bubble.classList.remove('is-pulse');
        void bubble.offsetWidth;
        bubble.classList.add('is-pulse');
        last = current;
      }
    });
    observer.observe(bubble, { childList: true, characterData: true, subtree: true });
  }
})();
